import { IconChevronDown } from '@tabler/icons-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'src/components/Button/Button'
import { Container } from 'src/components/Container/Container'
import { IconLoading } from 'src/components/Icons/IconLoading'
import { PostCard } from 'src/components/PostCards/PostCard'
import { useLocale } from 'src/contexts/LocaleContext'
import { fetchBlogCategoryPosts } from 'src/data/providers/fetchBlogCategoryPosts'
import { LOAD_MORE_POSTS } from 'src/selectors'

import styles from './PostCards.module.scss'

import type { Post } from 'src/types/Post'

interface PostCardsProps {
  posts: Post[]
  totalPages: number
  categoryId?: string
}

export const PostCards = ({ posts, totalPages, categoryId }: PostCardsProps) => {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [currentPosts, setCurrentPosts] = useState(posts)
  const [currentTotalPages, setCurrentTotalPages] = useState(totalPages)
  const [currentPage, setCurrentPage] = useState(1)
  const [isFetching, setIsFetching] = useState(false)

  const hasMorePages = currentTotalPages > currentPage

  const getNextPage = async () => {
    if (isFetching || !hasMorePages) {
      return
    }

    setIsFetching(true)

    const nextPageNumber = currentPage + 1
    const nextPostsResponse = await fetchBlogCategoryPosts(locale, Number(categoryId), nextPageNumber)

    setCurrentTotalPages(parseInt(nextPostsResponse.headers['x-wp-totalpages'], 10))
    setCurrentPosts(currentPosts.concat(nextPostsResponse.data))
    setCurrentPage(nextPageNumber)
    setIsFetching(false)
  }

  return (
    <>
      <Container className={styles.postList}>
        {currentPosts.map((post, i) => (
          <PostCard key={post.id} post={post} preload={i === 0} lazy={i !== 0} />
        ))}
      </Container>
      <Container className={styles.more}>
        {hasMorePages && (
          <Button data-cy={LOAD_MORE_POSTS} onClick={getNextPage} variant="secondary">
            {isFetching ? (
              <IconLoading />
            ) : (
              <>
                <IconChevronDown />
                {t('common.showMore')}
              </>
            )}
          </Button>
        )}
      </Container>
    </>
  )
}
